import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_CONFIG } from '../config/api.config';

export interface MetodoPago {
  id: string;
  nombre: string;
  tipo: string;
  estado: string;
  comision: number;
  api_key?: string;
  secret_key?: string;
}

export interface Precio {
  id?: string;
  precioid?: string;
  nombre_precio: string;
  precio_base: number;
  precio_ticket_final: number;
  precio_cargo_servicio: number;
  precio_cargo_servicio_final: number;
  precio_servicio_administrativo: number;
  iva_servicio_administrativo: number;
  precio_servicio_administrativo_final: number;
  precio_total: number;
  iva_ticket: number;
  iva_cargo_servicio: number;
  nombre_moneda: string;
  canal: string;
  publicado: boolean;
  disponible: boolean;
  default: boolean;
  tipoprecio?: string;
  desde: string | null;
  hasta: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TipoTicket {
  tipoticketid: string;
  eventoid: string;
  nombre_localidad: string;
  aforo_maximo: number;
  aforo_disponible: number;
  tickets_reservados: number;
  cargo_servicio: number;
  metodos_pago: MetodoPago[];
  precios: Precio[];
  estado: string;
  tiempo_expiracion_reserva: number | null;
  seatmapid?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Ticket {
  ticketid: string;
  secuencial: string;
  tipoticketid: string;
  eventoid: string;
  estado: string;
  origen: string;
  row?: number;
  col?: number;
  direction?: string;
  cartid?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SeatMapSeat {
  row: number;
  col: number;
  label: string;
  available: boolean;
  direction: string;
}

export interface SeatMap {
  seatmapid: string;
  nombre: string;
  rows: number;
  cols: number;
  seats: SeatMapSeat[];
  title?: string;
  frontLabel?: string;
  backLabel?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TipoTicketFuncion {
  eventofuncionid: string;
  eventoid: string;
  subeventoid: string;
  tipoticketid: string;
  fecha: string;
  hora: string;
  fecha_funcion: string;
  fecha_funcion_maxima_disponible_web: string;
  fecha_funcion_maxima_disponible_boxoffice: string;
}

export interface TipoTicketDetailed extends TipoTicket {
  tickets: Ticket[];
  seatmap: SeatMap | null;
  funcion?: TipoTicketFuncion;
  subevento?: {
    subeventoid: string;
    nombre: string;
    slug: string;
  };
}

export interface TiposTicketResponse {
  success: boolean;
  data: TipoTicket[];
}

export interface TipoTicketDetailedResponse {
  success: boolean;
  data: TipoTicketDetailed;
}

export interface TicketsByEstadoResponse {
  success: boolean;
  data: {
    tipoticketid: string;
    nombre_localidad: string;
    total_tickets: number;
    tickets_by_estado: {
      disponible: string[];
      reservado: string[];
      vendido: string[];
      validado: string[];
      anulado: string[];
      cortesia: string[];
      bloqueado: string[];
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class TicketsService {
  constructor(private http: HttpClient) {}

  private isWebActivePrice(precio: any): boolean {
    if (!precio || (precio.canal || '').toLowerCase() !== 'web') {
      return false;
    }

    const estadoActivo = (precio.estado || '').toLowerCase() === 'activo';
    const publicado = precio.publicado === true;
    const disponible = precio.disponible === true;

    return estadoActivo && publicado && disponible;
  }

  getPrecioId(precio: Precio | null | undefined): string {
    return precio?.precioid || precio?.id || '';
  }

  private normalizePrecio(precio: any): Precio {
    return {
      ...precio,
      id: precio?.id || precio?.precioid,
      precioid: precio?.precioid || precio?.id,
    } as Precio;
  }

  private normalizeTipoTicket<T extends { precios: Precio[] }>(tipoTicket: T): T {
    return {
      ...tipoTicket,
      precios: Array.isArray(tipoTicket?.precios)
        ? tipoTicket.precios.map(precio => this.normalizePrecio(precio))
        : []
    };
  }

  /**
   * Obtiene los tipos de ticket de un evento
   */
  getTiposTicketByEvento(eventoid: string): Observable<TiposTicketResponse> {
    const url = API_CONFIG.endpoints.tiposTicket.getByEvento.replace('{eventoid}', eventoid);
    console.log(`Fetching ticket types for event ID: ${eventoid} from URL: ${API_CONFIG.baseUrl}${url}`); 
    return this.http.get<TiposTicketResponse>(`${API_CONFIG.baseUrl}${url}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      }
    }).pipe(
      map(response => ({
        ...response,
        data: Array.isArray(response?.data)
          ? response.data.map(tipoTicket => this.normalizeTipoTicket(tipoTicket))
          : []
      }))
    );
  }

  /**
   * Obtiene un tipo de ticket por su ID con toda la información (tickets, seatmap)
   * Soporta autenticación mediante JWT, httponly cookies o solo API Key
   */
  getTipoTicketByIdForWeb(tipoticketid: string): Observable<TipoTicketDetailedResponse> {
    const url = API_CONFIG.endpoints.tiposTicket.getByIdForWeb.replace('{tipoticketid}', tipoticketid);
    console.log(`Fetching detailed ticket type for ID: ${tipoticketid} from URL: ${API_CONFIG.baseUrl}${url}`); 
    
    // El authInterceptor se encargará automáticamente de:
    // - Agregar withCredentials si está en modo httponly
    // - Agregar Authorization Bearer si hay token JWT
    // - Agregar x-guest-session-id si es necesario
    return this.http.get<TipoTicketDetailedResponse>(`${API_CONFIG.baseUrl}${url}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      }
      // No especificamos withCredentials aquí, el interceptor lo maneja
    }).pipe(
      map(response => {
        if (!response?.data) {
          return response;
        }

        const normalized = this.normalizeTipoTicket(response.data);
        const preciosWebActivos = (normalized.precios || []).filter(precio => this.isWebActivePrice(precio));

        if (preciosWebActivos.length === 0) {
          throw new HttpErrorResponse({
            status: 404,
            statusText: 'Not Found',
            url: `${API_CONFIG.baseUrl}${url}`,
            error: { message: 'Tipo ticket not found' }
          });
        }

        return {
          ...response,
          data: {
            ...normalized,
            precios: preciosWebActivos
          }
        };
      })
    );
  }

  /**
   * Obtiene los IDs de tickets agrupados por estado para un tipo de ticket
   */
  getTicketsByEstado(tipoticketid: string): Observable<TicketsByEstadoResponse> {
    const url = API_CONFIG.endpoints.tiposTicket.getTicketsByEstado.replace('{tipoticketid}', tipoticketid);
    console.log(`Fetching tickets by estado for tipo ticket ID: ${tipoticketid} from URL: ${API_CONFIG.baseUrl}${url}`);
    return this.http.get<TicketsByEstadoResponse>(`${API_CONFIG.baseUrl}${url}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      }
    });
  }

  /**
   * Calcula el máximo de tickets que se pueden comprar
   * Considerando el aforo disponible y el límite configurado por evento
   */
  getMaxTickets(aforoDisponible: number, maxPorCompra: number = 10): number {
    if (aforoDisponible === 0) return 0;
    return Math.min(aforoDisponible, maxPorCompra);
  }

  /**
   * Genera array de opciones para el selector de cantidad
   */
  getQuantityOptions(maxTickets: number): number[] {
    return Array.from({ length: maxTickets + 1 }, (_, i) => i);
  }
}
